import React, { useState } from 'react';
import './LandingPage.css';

function LandingPage({ onStart }) {
  const [showColumnReference, setShowColumnReference] = useState(false);

  const columns = [
    { name: 'd_abs', description: '3D distance of galaxy to cluster centre', unit: 'R₂₀₀' },
    { name: 'd_proj', description: 'Projected 2D distance of galaxy to cluster centre', unit: 'R₂₀₀' },
    { name: 'v_proj_veldisp', description: 'Projected 2D velocity in plane of sky, normalized by velocity dispersion', unit: 'unitless' },
    { name: 'v_los_veldisp', description: 'Line-of-sight velocity, normalized by velocity dispersion', unit: 'unitless' },
    { name: 'cos_v_ang', description: 'Cosine of angle between 2D velocity and distance to cluster centre (−1 = toward, +1 = away)', unit: 'unitless' },
    { name: 'mhalo', description: 'Log of dark matter halo mass (M₂₀₀) of the galaxy', unit: 'log(M☉)' },
    { name: 'mstar', description: 'Log of stellar mass of the galaxy', unit: 'log(M☉)' },
    { name: 'mgas', description: 'Log of gas mass in the galaxy', unit: 'log(M☉)' },
    { name: 'mclus', description: 'Log of dark matter halo mass (M₂₀₀) of the host cluster', unit: 'log(M☉)' },
    { name: 'R200', description: 'R₂₀₀ of the host cluster', unit: 'kpc' },
    { name: 'mag_diff', description: 'R-band magnitude gap between brightest and 2nd-brightest cluster galaxies (2D)', unit: 'mag' },
    { name: 'cosRAangle', description: 'Cosine of angle between galaxy major axis and cluster centre direction (2D)', unit: 'unitless' },
    { name: 'cosRAangle_3D', description: 'Cosine of angle between galaxy major axis and cluster centre direction (3D)', unit: 'unitless' },
    { name: 'axis_ratio', description: 'Minor-to-major axis ratio of galaxy, projected onto sky (1 = round)', unit: 'unitless' },
    { name: 'prolateness_3D', description: 'Ratio of intermediate to major axes in 3D (1 = oblate, 0 = prolate)', unit: 'unitless' },
    { name: 'half_mass_offset', description: 'Offset between inner and outer stellar centre of mass, in half-mass radii (2D)', unit: 'r_half' },
    { name: 'half_mass_ellipt', description: 'Ratio of inner to outer stellar ellipticity (1 = same shape)', unit: 'unitless' },
    { name: 'cosBCGmajorangle', description: 'Cosine of angle between galaxy→BCG vector and BCG major axis (2D)', unit: 'unitless' },
    { name: 'BCG_axis_ratio', description: 'Minor-to-major axis ratio of the BCG (1 = round)', unit: 'unitless' },
    { name: 'neighbor_dist_4', description: 'Distance to 4th nearest neighbor galaxy, normalized by cluster R₂₀₀', unit: 'R₂₀₀' }
  ];

  return (
    <div className="landing-page">
      <div className="landing-container">
        <div className="hero-section">
          <h1 className="landing-title">
            <span className="title-emoji">🌌</span>
            Backsplash Galaxy Classifier
          </h1>
          <p className="landing-subtitle">
            Machine learning-powered classification of backsplash and infalling galaxies in galaxy clusters
          </p>
        </div>

        <div className="top-section">
          <div className="workflow-section-top">
            <h3>How It Works</h3>
            <div className="workflow-steps-compact">
              <div className="workflow-step-compact">
                <div className="step-number-compact">1</div>
                <div className="step-text">Upload CSV</div>
              </div>
              <div className="workflow-arrow-compact">→</div>
              <div className="workflow-step-compact">
                <div className="step-number-compact">2</div>
                <div className="step-text">Map Columns</div>
              </div>
              <div className="workflow-arrow-compact">→</div>
              <div className="workflow-step-compact">
                <div className="step-number-compact">3</div>
                <div className="step-text">Train Model</div>
              </div>
              <div className="workflow-arrow-compact">→</div>
              <div className="workflow-step-compact">
                <div className="step-number-compact">4</div>
                <div className="step-text">Get Results</div>
              </div>
            </div>
          </div>

          <div className="cta-section-top">
            <button className="start-button-compact" onClick={onStart}>
              Get Started →
            </button>
            <p className="cta-subtitle-compact">Ready in less than 5 minutes</p>
          </div>
        </div>

        <div className="info-section">
          <div className="info-card">
            <div className="info-icon">🎯</div>
            <h3>What This App Does</h3>
            <p>
              This tool uses Random Forest machine learning to classify galaxies in galaxy clusters as either 
              <strong className="backsplash-text"> Backsplash</strong> (galaxies that have passed through the cluster and are now moving outward) 
              or <strong className="infalling-text">Infalling</strong> (galaxies falling into the cluster for the first time).
            </p>
            <p>
              The model is trained on simulated galaxy data with known classifications and can predict the 
              classification of real observational data based on galaxy properties.
            </p>
          </div>

          <div className="info-card">
            <div className="info-icon">📁</div>
            <h3>Required Input Format</h3>
            <ul className="format-list">
              <li><strong>File type:</strong> CSV (comma-separated values)</li>
              <li><strong>Column names:</strong> First row must contain column headers</li>
              <li><strong>Data format:</strong> Numeric values for galaxy properties</li>
              <li><strong>Columns:</strong> The more matching columns you provide, the better the classification accuracy</li>
              <li><strong>Data privacy:</strong> Your uploaded file is deleted as soon as you download your results (Step 6), and automatically within 30 minutes otherwise — it is never stored permanently.</li>
            </ul>
            <div className="format-example">
              <strong>Example CSV structure:</strong>
              <pre>
d_proj,v_los_veldisp,mstar,color,sfr{'\n'}
0.52,0.31,10.5,0.82,-0.45{'\n'}
1.23,-0.15,9.8,1.05,-1.20{'\n'}
...
              </pre>
            </div>
          </div>

          <div className="info-card column-reference-card">
            <div className="info-icon">📊</div>
            <h3>Column Reference</h3>
            <p className="reference-description">
              Your CSV can include any combination of these columns. Column names will be automatically matched 
              (e.g., "projected_distance", "dist_proj", or "d_proj" all work).
            </p>
            
            <button 
              className="toggle-reference-btn"
              onClick={() => setShowColumnReference(!showColumnReference)}
            >
              {showColumnReference ? '▼ Hide Column List' : '▶ Show Column List'}
            </button>

            {showColumnReference && (
              <div className="column-table">
                <div className="column-table-header">
                  <div className="col-name">Column Name</div>
                  <div className="col-desc">Description</div>
                  <div className="col-unit">Unit</div>
                </div>
                {columns.map((col, idx) => (
                  <div key={idx} className="column-row">
                    <div className="col-name"><code>{col.name}</code></div>
                    <div className="col-desc">{col.description}</div>
                    <div className="col-unit">{col.unit}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LandingPage;
